import React, { ReactNode, useEffect, useId, useRef } from 'react';

import { createPortal } from 'react-dom';

import { CLOSE_ICON } from '../../../constants/images';
import type { GenericVoidFunction } from '../../../types/genericTypes';
import { CustomSize } from '../../../types/globals';
import IconButton from '../../Buttons/IconButton/IconButton';

import './CustomModal.scss';

interface ICustomModalProps {
  onClose?: (event: React.SyntheticEvent | React.MouseEvent, reason?: string) => void;
  open: boolean;
  customClass?: string;
  onBackdropClick?: GenericVoidFunction;
  title?: ReactNode;
  size?: CustomSize;
  hideCloseButton?: boolean;
  disableEscapeClose?: boolean;
}

export const CustomModal = ({
  open,
  onClose,
  customClass,
  children,
  onBackdropClick,
  title,
  size = 'm',
  hideCloseButton = false,
  disableEscapeClose = false
}: React.PropsWithChildren<ICustomModalProps>) => {
  const titleId = useId();
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    const handleClick = (event: MouseEvent) => {
      if (event.target === dialog && onBackdropClick) {
        onBackdropClick(event as unknown as React.MouseEvent);
      }
    };

    const handleCancel = (event: Event) => event.preventDefault();

    dialog.addEventListener('click', handleClick);
    dialog.addEventListener('cancel', handleCancel);
    return () => {
      dialog.removeEventListener('click', handleClick);
      dialog.removeEventListener('cancel', handleCancel);
    };
  }, [open, onBackdropClick]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose && !disableEscapeClose) {
        onClose(event as unknown as React.SyntheticEvent, 'escapeKeyDown');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose, disableEscapeClose]);

  if (!open) {
    return null;
  }

  const showCloseButton = !!onClose && !hideCloseButton;
  const hasHeader = title || showCloseButton;

  return createPortal(
    <dialog
      ref={dialogRef}
      className={`custom-modal custom-modal--${size} ${customClass ?? ''}`}
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      data-testid="modal-backdrop"
    >
      <div className="custom-modal__container">
        {hasHeader && (
          <div className="custom-modal__header">
            {title && (
              <div className="custom-modal__header-title" id={titleId}>
                {title}
              </div>
            )}
            {showCloseButton && onClose && (
              <IconButton label="Close modal" icon={CLOSE_ICON} action={onClose} variant="transparent" size="s" />
            )}
          </div>
        )}
        <div className="custom-modal__content">{children}</div>
      </div>
    </dialog>,
    document.body
  );
};

export default CustomModal;
